import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import DiagnosisService from '../../services/diagnosisService';
import Button from '../common/Button';
import './Diagnoses.css';

const Diagnoses = () => {
  const auth = useAuth();
  const [diagnoses, setDiagnoses] = useState([]);
  const [filteredDiagnoses, setFilteredDiagnoses] = useState([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingDiagnosis, setEditingDiagnosis] = useState(null);
  const [deletingDiagnosis, setDeletingDiagnosis] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    color: '#FF0000',
    isCompleteTooth: false
  });

  const loadDiagnoses = useCallback(async () => {
    try {
      setInitialLoading(true);
      const token = auth.user?.access_token;
      const response = await DiagnosisService.getAllDiagnoses(token);
      setDiagnoses(response.data || []);
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Error al cargar los diagnósticos'
      });
    } finally {
      setInitialLoading(false);
    }
  }, [auth.user?.access_token]);

  // Load diagnoses on component mount
  useEffect(() => {
    loadDiagnoses();
  }, [loadDiagnoses]);

  // Filter diagnoses when search filter or diagnoses change
  useEffect(() => {
    if (!searchFilter.trim()) {
      setFilteredDiagnoses(diagnoses);
    } else {
      const filtered = diagnoses.filter(diagnosis =>
        diagnosis.name.toLowerCase().includes(searchFilter.toLowerCase())
      );
      setFilteredDiagnoses(filtered);
    }
  }, [searchFilter, diagnoses]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSearchChange = (e) => setSearchFilter(e.target.value);
  const clearSearch = () => setSearchFilter('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const token = auth.user?.access_token;
      if (editingDiagnosis) {
        await DiagnosisService.updateDiagnosis(editingDiagnosis.id, formData, token);
        setMessage({ type: 'success', text: 'Diagnóstico actualizado exitosamente' });
      } else {
        await DiagnosisService.createDiagnosis(formData, token);
        setMessage({ type: 'success', text: 'Diagnóstico creado exitosamente' });
      }
      await loadDiagnoses();
      handleCloseModal();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: editingDiagnosis ? 'Error al actualizar el diagnóstico' : 'Error al crear el diagnóstico' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddDiagnosis = () => {
    setEditingDiagnosis(null);
    setFormData({ name: '', color: '#FF0000', isCompleteTooth: false });
    setShowModal(true);
    setMessage({ type: '', text: '' });
  };

  const handleEditDiagnosis = (diagnosis) => {
    setEditingDiagnosis(diagnosis);
    setFormData({ 
      name: diagnosis.name, 
      color: diagnosis.color, 
      isCompleteTooth: !!diagnosis.isCompleteTooth 
    });
    setShowModal(true);
    setMessage({ type: '', text: '' });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingDiagnosis(null);
    setFormData({ name: '', color: '#FF0000', isCompleteTooth: false });
    setMessage({ type: '', text: '' });
  };

  const handleDeleteDiagnosis = (diagnosis) => {
    setDeletingDiagnosis(diagnosis);
    setShowDeleteModal(true);
    setMessage({ type: '', text: '' });
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletingDiagnosis(null);
    setMessage({ type: '', text: '' });
  };

  const confirmDelete = async () => {
    if (!deletingDiagnosis) return;
    setIsLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const token = auth.user?.access_token;
      await DiagnosisService.deleteDiagnosis(deletingDiagnosis.id, token);
      setMessage({ type: 'success', text: 'Diagnóstico eliminado exitosamente' });
      await loadDiagnoses();
      handleCloseDeleteModal();
    } catch (error) {
      setMessage({ type: 'error', text: 'Error al eliminar el diagnóstico' });
    } finally {
      setIsLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="diagnoses-container">
        <div className="diagnoses-loading">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="diagnoses-container">
      <div className="diagnoses-header">
        <div className="header-content">
          <h1>Diagnósticos</h1>
          <p>Gestiona los diagnósticos disponibles en tu clínica</p>
        </div>
        <button 
          className="btn btn-primary add-btn"
          onClick={handleAddDiagnosis}
        >
          Agregar Diagnóstico
        </button>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Filters */}
      <div className="diagnoses-filters">
        <div className="search-container">
          <input
            type="text"
            placeholder="Buscar por nombre de diagnóstico..."
            value={searchFilter}
            onChange={handleSearchChange}
            className="search-input"
          />
          {searchFilter && (
            <button
              className="clear-search-btn"
              onClick={clearSearch}
              title="Limpiar búsqueda"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Diagnoses Table */}
      <div className="diagnoses-table-container">
        {filteredDiagnoses.length === 0 ? (
          <div className="no-diagnoses">
            <p>No se encontraron diagnósticos</p>
            {searchFilter && (
              <button className="btn-secondary" onClick={clearSearch}>
                Limpiar filtros
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <table className="diagnoses-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Color</th>
                  <th>Aplica a</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredDiagnoses.map((diagnosis) => {
                  return (
                    <tr key={diagnosis.id}>
                      <td className="diagnosis-name">
                        <div className="diagnosis-details">
                          <span className="name">{diagnosis.name}</span>
                        </div>
                      </td>
                      <td className="diagnosis-color">
                        <div className="color-display">
                          <span 
                            className="color-dot" 
                            style={{ backgroundColor: diagnosis.color }}
                          ></span>
                        </div>
                      </td>
                      <td className="diagnosis-type">
                        <span className={`type-badge ${diagnosis.isCompleteTooth ? 'complete' : 'individual'}`}>
                          {diagnosis.isCompleteTooth ? 'Diente completo' : 'Por cara'}
                        </span>
                      </td>
                      <td className="diagnosis-actions">
                        <button
                          className="btn btn-secondary btn-sm edit-btn"
                          onClick={() => handleEditDiagnosis(diagnosis)}
                          title="Editar diagnóstico"
                        >
                          Editar
                        </button>
                        <button
                          className="btn btn-danger btn-sm delete-btn"
                          onClick={() => handleDeleteDiagnosis(diagnosis)}
                          title="Eliminar diagnóstico"
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="diagnoses-mobile-cards">
              {filteredDiagnoses.map((diagnosis) => (
                <div key={diagnosis.id} className="diagnosis-card">
                  <div className="diagnosis-card-header">
                    <div className="diagnosis-card-title">{diagnosis.name}</div>
                  </div>
                  <div className="diagnosis-card-content">
                    <div className="diagnosis-card-row">
                      <span className="diagnosis-card-label">Color:</span>
                      <span className="diagnosis-card-value">
                        <span 
                          className="color-dot" 
                          style={{ backgroundColor: diagnosis.color }}
                        ></span>
                      </span>
                    </div>
                    <div className="diagnosis-card-row">
                      <span className="diagnosis-card-label">Aplica a:</span>
                      <span className="diagnosis-card-value">
                        <span className={`type-badge ${diagnosis.isCompleteTooth ? 'complete' : 'individual'}`}>
                          {diagnosis.isCompleteTooth ? 'Diente completo' : 'Por cara'}
                        </span>
                      </span>
                    </div>
                  </div>
                  <div className="diagnosis-card-actions">
                    <button
                      className="btn btn-secondary btn-sm edit-btn"
                      onClick={() => handleEditDiagnosis(diagnosis)}
                      title="Editar diagnóstico"
                    >
                      Editar
                    </button>
                    <button
                      className="btn btn-danger btn-sm delete-btn"
                      onClick={() => handleDeleteDiagnosis(diagnosis)}
                      title="Eliminar diagnóstico"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{editingDiagnosis ? 'Editar Diagnóstico' : 'Agregar Nuevo Diagnóstico'}</h3>
              <button className="modal-close" onClick={handleCloseModal}>✕</button>
            </div>
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="name">Nombre <span className="required">*</span></label>
                  <input 
                    type="text" 
                    id="name" 
                    name="name" 
                    value={formData.name} 
                    onChange={handleInputChange} 
                    required 
                    placeholder="Ej: Caries, Fractura, etc." 
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="color">Color <span className="required">*</span></label>
                  <div className="color-picker-row">
                    <input 
                      type="color" 
                      id="color" 
                      name="color" 
                      value={formData.color} 
                      onChange={handleInputChange} 
                      required 
                    />
                    <input 
                      type="text" 
                      className="color-hex" 
                      value={formData.color} 
                      onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))} 
                    />
                  </div>
                </div>
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      id="isCompleteTooth"
                      name="isCompleteTooth"
                      checked={formData.isCompleteTooth}
                      onChange={handleInputChange}
                    />
                    <span>Aplica a diente completo</span>
                  </label>
                </div>
              </div>
              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-secondary" 
                  onClick={handleCloseModal} 
                  disabled={isLoading}
                >
                  Cancelar
                </button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={isLoading}
                  icon="fas fa-save"
                >
                  {isLoading ? (editingDiagnosis ? 'Actualizando...' : 'Creando...') : (editingDiagnosis ? 'Actualizar' : 'Crear')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal delete-modal">
            <div className="modal-header">
              <h3>Confirmar Eliminación</h3>
              <button className="modal-close" onClick={handleCloseDeleteModal}>✕</button>
            </div>
            <div className="modal-content">
              <p>¿Estás seguro de que deseas eliminar el diagnóstico <strong>"{deletingDiagnosis?.name}"</strong>?</p>
              <p className="warning-text">Esta acción no se puede deshacer.</p>
            </div>
            <div className="modal-actions">
              <button 
                type="button" 
                className="btn-secondary" 
                onClick={handleCloseDeleteModal} 
                disabled={isLoading}
              >
                Cancelar
              </button>
              <button 
                type="button" 
                className="btn-danger" 
                onClick={confirmDelete} 
                disabled={isLoading}
              >
                {isLoading ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Diagnoses;